import { ConversationalChatVisibility, Platform, LoginStatus } from '@klant/conversational-connector/klant-conversational-connector.model';
import { ConversationService } from '@klant/conversational-connector/services/conversation/conversation.service';
import { ConversationalConnectorAnalyticsService } from '@klant/conversational-connector/services/analytics/analytics.service';

import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';

import { ChannelName } from '@ap/mijn-kanaalconfiguratie';
import { ApError } from '@ap/error';
import { ApContentService } from '@ap/content';

import { ChatSessionStore } from '../../signal-stores/chat-sesion-store/chat-session.store';
import { OperationalCheckService } from '../../shared/services/operational-check.service';
import { ConversationalChatStore } from '../../signal-stores/conversational-chat-store/conversational-chat.store';

import { RETAIL_CONVERSATIONAL_OPERATIONAL } from './retail-klant-conversational.constants';
import content from './retail-klant-conversational.content.json';

@Component({
    selector: 'retail-klant-conversational',
    templateUrl: './retail-klant-conversational.component.html',
    standalone: false
})
export class RetailKlantConversationalComponent implements OnInit {
    private readonly chatSessionStore = inject(ChatSessionStore);
    private readonly conversationalChatStore = inject(ConversationalChatStore);
    private readonly conversationService = inject(ConversationService);
    private readonly apContentService = inject(ApContentService);
    private readonly conversationalConnectorAnalyticsService = inject(ConversationalConnectorAnalyticsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly operationalCheckService = inject(OperationalCheckService);

    public showError = computed(() => this.chatSessionStore.errorGettingChatSessionIdChat() || this.isOperational() === false);
    public showConnector = computed(() => !this.showError() && this.isOperational() === true);
    public platform = signal<Platform>(Platform.RETAIL);
    public isOperational = this.operationalCheckService.isOperational;

    public takeoverError = signal<ApError>(null);

    private chatVisibility$ = toObservable(this.conversationService.getChatVisibility);
    private isOperational$ = toObservable(this.isOperational);

    public ngOnInit(): void {
        this.apContentService.addContentItems(content);
        this.conversationalChatStore.setPlatform(Platform.RETAIL);

        this.setStaticVariables();
        this.checkIsOperational();
        this.observeShouldInitializeAnonymousChat();
    }

    private observeShouldInitializeAnonymousChat(): void {
        combineLatest([this.chatVisibility$, this.isOperational$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([chatVisibility, isOperational]) => {
                if (this.shouldIniatializeAnonymousChat(chatVisibility, isOperational)) {
                    this.initializeAnonymousChat();
                }
            });
    }

    private shouldIniatializeAnonymousChat(chatVisibility: ConversationalChatVisibility, isOperational: boolean): boolean {
        return chatVisibility === ConversationalChatVisibility.OPEN && isOperational && !this.chatSessionStore.chatSession();
    }

    private initializeAnonymousChat(): void {
        this.conversationalConnectorAnalyticsService.pushAnalyticsPageview(LoginStatus.ANONYMOUS);
        this.chatSessionStore.patchChatSession({ loginStatus: LoginStatus.ANONYMOUS });
    }

    private setStaticVariables(): void {
        this.takeoverError.set(
            new ApError(this.apContentService.get('retail.klant.conversational.takeover.error.message'), undefined, undefined, false)
        );
    }

    private checkIsOperational(): void {
        this.operationalCheckService
            .checkIsOperational({
                channelName: ChannelName.MIJN_KLANT,
                configKey: RETAIL_CONVERSATIONAL_OPERATIONAL
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

    public setStatusChangeSubscriptions(event): void {
        this.conversationService.setConversationVisibility(event);
    }
}
